import { Badge, Box, SxProps, Tab, Tabs, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactNode, SyntheticEvent, useCallback, useState } from 'react';
import { CommunityConnections } from './components/CommunityConnections';
import { CommunityDiscover } from './components/CommunityDiscover';
import { CommunityOptInShell } from './components/CommunityOptIn';
import { CommunityOptInProvider } from './hook/useCommunityOptIn';
import { useCheckNewConnection } from '../notification/hooks/useCheckNewConnection';
import {
  useAnalytics,
  METRICS_COMMUNITY_CONNECTIONS_ACCESS,
} from '@xolv-mybrightlink/common';

export const CommunityPage = () => {
  const [currentTab, setCurrentTab] = useState(ConnectionTab.DISCOVER);

  const hasNewConnection = useCheckNewConnection();
  const analytics = useAnalytics();
  const handleChangeTab = useCallback(
    (_: SyntheticEvent, newTab: number) => {
      analytics.trackAction(METRICS_COMMUNITY_CONNECTIONS_ACCESS);
      setCurrentTab(newTab);
    },
    [analytics],
  );
  return (
    <CommunityOptInProvider>
      <CommunityOptInShell>
        <Box sx={{ p: 10, minHeight: '100vh', position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 45,
              left: 40,
            }}
          >
            <Typography variant='subtitle1' sx={{ fontWeight: 500 }}>
              <FormattedMessage id='community.title' />
            </Typography>
          </Box>
          <Box>
            <Tabs value={currentTab} onChange={handleChangeTab} centered>
              <Tab
                label={<TabName messageTitleId='community.discover.title' />}
              />
              <Tab
                label={
                  <TabName
                    messageTitleId='community.connection.title'
                    hasBadge={hasNewConnection}
                  />
                }
              />
            </Tabs>
            <TabPanel value={currentTab} index={ConnectionTab.DISCOVER}>
              <CommunityDiscover />
            </TabPanel>
            <TabPanel value={currentTab} index={ConnectionTab.CONNECTIONS}>
              <CommunityConnections />
            </TabPanel>
          </Box>
        </Box>
      </CommunityOptInShell>
    </CommunityOptInProvider>
  );
};

type TabNameProps = {
  messageTitleId: string;
  sx?: SxProps;
  hasBadge?: boolean;
};

const TabName = ({ messageTitleId, sx, hasBadge }: TabNameProps) => (
  <Badge
    invisible={!hasBadge}
    variant='dot'
    color='secondary'
    sx={{
      '& .MuiBadge-badge': {
        backgroundColor: '#d00070',
        right: -10,
      },
    }}
  >
    <Typography sx={{ fontWeight: 500, ...sx }}>
      <FormattedMessage id={messageTitleId} />
    </Typography>
  </Badge>
);

type TabPanelProps = {
  children?: ReactNode;
  index: number;
  value: number;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index } = props;

  return (
    <Box
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && children}
    </Box>
  );
};

enum ConnectionTab {
  DISCOVER,
  CONNECTIONS,
}
